import type { TemplateSet } from '@sosocio/creator-types/app';
import type {
	OfferingModel, ProductModel,
} from '@sosocio/creator-types/database';
import type {
	PageModel, PageObjectModel,
} from '@sosocio/creator-types/project';
import objectHash from 'object-hash';
import { scalePageTemplateLayout } from './page-template-layout';

export interface LegacyResizedPageLayoutRepair {
	templateLayout: TemplateSet;
	templateSetId: string;
}

export interface LegacyResizedPageLayoutRepairOptions {
	objectModels: readonly PageObjectModel[];
	offeringModel: Pick<OfferingModel, 'bleedmargin' | 'configdpi' | 'groupid' | 'height' | 'typeid' | 'variantid' | 'virtual' | 'width'>;
	pageModel: PageModel;
	productModel: Pick<ProductModel, 'group' | 'themeid' | 'typeid' | 'variantid'>;
}

export const LEGACY_RESIZED_PAGE_LAYOUT_REPAIR_ID = 'legacy-resized-single-slot-1181-to-1535-v1';

/**
 * Restores one independently verified legacy generated layout after the old
 * theme-changing resize discarded its set reference. The saved photo frame
 * confirms the known transform; it never supplies the recovered slot geometry.
 * Unmatched references, manual layouts and other layout families are untouched.
 *
 * @param options Current project, offering, page and its complete object collection.
 * @param options.objectModels Every object belonging to the page.
 * @param options.offeringModel Current offering whose geometry must match the verified resize.
 * @param options.pageModel Persisted page whose active slot reference was lost.
 * @param options.productModel Project identity restricting the historical layout family.
 * @returns Detached complete layout and set reference, or undefined when any signature differs.
 */
export function getLegacyResizedPageLayoutRepair({
	objectModels,
	offeringModel,
	pageModel,
	productModel,
}: LegacyResizedPageLayoutRepairOptions): LegacyResizedPageLayoutRepair | undefined {
	if (
		productModel.group !== 101
		|| productModel.typeid !== 1101
		|| productModel.variantid !== 1
		|| productModel.themeid !== 2296
		|| !isLegacyResizedPageLayoutOffering(offeringModel)
		|| pageModel.template !== 1809
		|| pageModel.width !== 1535
		|| pageModel.height !== 1535
		|| pageModel.offset !== 35
		|| pageModel.templateMarginAroundEdge !== 72
		|| pageModel.templateMarginBetweenPositions !== 36
		|| pageModel.customLayout !== false
		|| pageModel.templateSetId != null
		|| pageModel.templateLayout != null
		|| pageModel.offeringId != null
		|| pageModel.objectList?.length !== 1
		|| objectModels.length !== 1
	) {
		return undefined;
	}

	const photo = objectModels[0];
	const originalGeometry = {
		x: 72,
		y: 72,
		width: 1037,
		height: 1037,
	};
	const originalId = objectHash(originalGeometry);

	if (
		originalId !== '06bc85be55616b9ec9ae3636c10df703f625a9f0'
		|| pageModel.objectList[0] !== photo.id
		|| photo.type !== 'photo'
		|| typeof photo.photoid !== 'number'
		|| !Number.isInteger(photo.photoid)
		|| photo.photoid <= 0
		|| photo.templatestateid !== originalId
		|| photo.fillMethod !== 'cover'
		|| photo.rotate !== 0
		|| photo.decorative
		|| photo.transformable !== 1
		|| photo.z_axis !== 0
		|| photo.mask !== null
		|| photo.borderwidth !== 0
		|| photo.borderimage
	) {
		return undefined;
	}

	/**
	 * This is the complete canonical one-slot grid for an 1181-square page at
	 * 300 dpi and 72/36 margins. The source geometry hash identifies that slot
	 * uniquely among all supported one-to-five-position generated layouts.
	 */
	const originalLayout: TemplateSet = {
		id: originalId,
		templateId: 1809,
		positions: [
			{
				...originalGeometry,
				id: originalId,
				z: 0,
				angle: 0,
				borderwidth: 0,
				bordercolor: '#000000',
				transformable: true,
				available: true,
				type: 'photo',
				mask: null,
				flag: true,
				autorotate: false,
				styleable: true,
				overlap_top: 0,
				overlap_right: 0,
				overlap_bottom: 0,
				overlap_left: 0,
				fillMethod: 'cover',
			},
		],
	};
	const templateLayout = scalePageTemplateLayout(
		originalLayout,
		{
			width: 1181,
			height: 1181,
			offset: 35,
		},
		{
			width: pageModel.width,
			height: pageModel.height,
			offset: pageModel.offset,
			template: pageModel.template,
		},
	);
	const [position] = templateLayout.positions;
	const frameMatches = [
		[
			position.x,
			photo.x_axis,
		],
		[
			position.y,
			photo.y_axis,
		],
		[
			position.width,
			photo.width,
		],
		[
			position.height,
			photo.height,
		],
	].every(([
		expected,
		actual,
	]) => Number.isFinite(actual) && Math.abs(expected - actual) < 1e-9);

	if (!frameMatches) {
		return undefined;
	}

	return {
		templateLayout,
		templateSetId: originalId,
	};
}

/**
 * Checks the offering portion of the verified legacy resize signature before
 * callers inspect live pages or parse Undo entries. Page and project identity
 * remain independent checks for each historical state that could be repaired.
 *
 * @param offeringModel Offering whose product family and geometry must match the known resize.
 * @returns Whether this offering can contain a layout eligible for the legacy repair.
 */
export function isLegacyResizedPageLayoutOffering(
	offeringModel: LegacyResizedPageLayoutRepairOptions['offeringModel'],
): boolean {
	return offeringModel.groupid === 101
		&& offeringModel.typeid === 1101
		&& offeringModel.variantid === 1
		&& offeringModel.virtual === 0
		&& offeringModel.configdpi === 300
		&& offeringModel.width === 1535
		&& offeringModel.height === 1535
		&& offeringModel.bleedmargin === 35;
}
